import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle, ChevronLeft, ChevronRight, Save, RefreshCw } from "lucide-react";

/**
 * DailyPnLCard
 * ---------------------------------------------------------------------------
 * Daily Profit & Loss panel for the admin dashboard.
 *
 * Pulls /api/admin/daily-pnl?date=YYYY-MM-DD and shows:
 *   • Revenue  (paid orders for the UK day)
 *   • COGS     (Σ qty × supplier_cost per item)
 *   • Gross    (revenue − COGS)
 *   • Ad spend (editable, persisted via /admin/daily-pnl/ad-spend)
 *   • Net      (gross − ad spend)
 *
 * Designed so David can land on the admin dashboard each morning, see
 * exactly what yesterday earned, edit his ad spend in one click, and
 * navigate day-by-day with arrow controls.
 */

const ukToday = () => {
  // Get "today" in UK time, not the user's local timezone — so a 2am-in-LA
  // admin checking the dashboard still sees the right UK day.
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(new Date()); // "YYYY-MM-DD"
};

const fmtPrettyDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
    timeZone: "UTC",
  });
};

const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};

export default function DailyPnLCard() {
  const [date, setDate] = useState(ukToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adSpendInput, setAdSpendInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data: pnl } = await api.get(`/admin/daily-pnl?date=${d}`);
      setData(pnl);
      setAdSpendInput(String(pnl.ad_spend ?? 0));
    } catch (err) {
      console.error("[DailyPnL] load failed", err);
      toast.error("Couldn't load daily P&L");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const saveAdSpend = async () => {
    const num = parseFloat(adSpendInput);
    if (Number.isNaN(num) || num < 0) {
      toast.error("Ad spend must be a positive number");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/daily-pnl/ad-spend", { date, ad_spend: num });
      toast.success(`Ad spend saved: £${num.toFixed(2)}`);
      await load(date);
    } catch (err) {
      console.error("[DailyPnL] save failed", err);
      toast.error("Couldn't save ad spend");
    } finally {
      setSaving(false);
    }
  };

  const isToday = date === ukToday();
  const netColour = (n) => n > 0 ? "text-[#A6FF00]" : n < 0 ? "text-[#EF4444]" : "text-[#F7FAFC]";

  return (
    <div className="pg-card p-6 mb-8" data-testid="daily-pnl-card">
      {/* ───── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="pg-micro mb-1 flex items-center gap-2">
            <TrendingUp size={11} className="text-[#A6FF00]" />
            // Daily P&amp;L
          </div>
          <h2
            className="text-[#F7FAFC] pg-heading text-xl"
            style={{ fontFamily: "'Rajdhani'" }}
          >
            {fmtPrettyDate(date)}{" "}
            {isToday && (
              <span className="text-[10px] pg-mono uppercase tracking-[0.22em] text-[#A6FF00] align-middle ml-2">
                · Today
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setDate(addDays(date, -1))}
            className="pg-btn-ghost text-[10px] uppercase tracking-[0.18em] px-2 py-1.5"
            data-testid="pnl-prev-day"
            title="Previous day"
          >
            <ChevronLeft size={14} />
          </button>
          <input
            type="date"
            value={date}
            max={ukToday()}
            onChange={(e) => setDate(e.target.value)}
            className="pg-input text-xs py-1.5 px-2"
            data-testid="pnl-date-picker"
          />
          <button
            type="button"
            onClick={() => setDate(addDays(date, +1))}
            disabled={isToday}
            className="pg-btn-ghost text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 disabled:opacity-30"
            data-testid="pnl-next-day"
            title="Next day"
          >
            <ChevronRight size={14} />
          </button>
          <button
            type="button"
            onClick={() => load(date)}
            className="pg-btn-ghost text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 ml-1"
            disabled={loading}
            data-testid="pnl-refresh"
            title="Refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ───── Numbers row ────────────────────────────────────────────── */}
      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Tile label="Revenue" value={data.revenue} colour="lime"
                  sub={`${data.orders_count} order${data.orders_count === 1 ? "" : "s"}`}
                  testid="pnl-revenue" />
            <Tile label="COGS" value={data.cogs} colour="cyan"
                  sub="cost of vials sold" negative testid="pnl-cogs" />
            <Tile label="Gross Profit" value={data.gross_profit} colour="lime"
                  sub="revenue − COGS" testid="pnl-gross" />
            <AdSpendTile
              value={adSpendInput}
              onChange={setAdSpendInput}
              onSave={saveAdSpend}
              saving={saving}
              persistedAt={data.ad_spend_updated_at}
            />
            <Tile label="Net Profit" value={data.net_profit} colour="lime"
                  sub={`${data.net_margin_pct.toFixed(1)}% margin`}
                  big colourOverride={netColour(data.net_profit)} testid="pnl-net" />
          </div>

          {/* ───── Missing-cost warning ──────────────────────────────── */}
          {data.missing_cost_skus && data.missing_cost_skus.length > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-sm bg-[#EF4444]/[0.08] border border-[#EF4444]/30" data-testid="pnl-missing-costs">
              <AlertTriangle size={14} className="text-[#EF4444] shrink-0 mt-0.5" />
              <div className="text-[11px] text-[#FCA5A5] leading-relaxed">
                <strong>COGS incomplete.</strong> Supplier cost is missing for{" "}
                <strong>{data.missing_cost_skus.join(", ")}</strong>.
                Add a cost-per-vial under Admin → Products → Edit → Supplier cost.
                Gross profit shown above is over-stated by the missing units.
              </div>
            </div>
          )}

          <div className="mt-4 text-[10px] pg-mono text-[#9CA8B8] uppercase tracking-[0.18em]">
            UK timezone · Excludes test orders and synthetic emails
          </div>
        </>
      ) : (
        <div className="text-[#9CA8B8] pg-mono py-8 text-center text-sm">Loading…</div>
      )}
    </div>
  );
}

function Tile({ label, value, colour = "lime", sub, big = false, negative = false, colourOverride, testid }) {
  const limeOrCyan = colour === "lime" ? "text-[#A6FF00]" : "text-[#00C2D6]";
  const finalColour = colourOverride || limeOrCyan;
  const sign = negative && value > 0 ? "−" : "";
  return (
    <div className="border border-[#1E2E44] rounded-sm p-3" data-testid={testid}>
      <div className="pg-micro mb-1">{label}</div>
      <div
        className={`pg-display ${big ? "text-3xl" : "text-2xl"} ${finalColour}`}
        style={{ fontFamily: "'Exo 2'" }}
      >
        {sign}£{Math.abs(value).toFixed(2)}
      </div>
      {sub && (
        <div className="text-[10px] text-[#9CA8B8] mt-1">{sub}</div>
      )}
    </div>
  );
}

function AdSpendTile({ value, onChange, onSave, saving, persistedAt }) {
  return (
    <div className="border border-[#A6FF00]/30 rounded-sm p-3 bg-[#A6FF00]/[0.03]" data-testid="pnl-ad-spend-tile">
      <div className="pg-micro mb-1 flex items-center justify-between">
        <span>Ad Spend</span>
        {persistedAt && (
          <span className="text-[9px] text-[#5EEAD4]">saved</span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[#9CA8B8] text-lg pg-display">£</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
          }}
          placeholder="0.00"
          className="flex-1 bg-transparent border-none text-[#F7FAFC] pg-display text-2xl outline-none p-0 min-w-0"
          style={{ fontFamily: "'Exo 2'" }}
          data-testid="pnl-ad-spend-input"
        />
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] py-1 px-2 rounded-sm border border-[#A6FF00]/40 text-[#A6FF00] hover:bg-[#A6FF00]/10 transition-colors flex items-center gap-1.5 disabled:opacity-50"
        data-testid="pnl-ad-spend-save"
      >
        <Save size={10} />
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
